/*
 * `[date]`, brought forward in the browser.
 *
 * WordPress's `[date]` shortcode (storefront-zager-child/functions.php:893) is
 * `date($format)` — evaluated on EVERY request. The migration evaluates it once,
 * when `transform` runs, deliberately and through one shared implementation
 * (migration/src/transform/shortcodes.ts `replaceDate`). That is right for a
 * stored body, but it means the value is the date of the last load.
 *
 * On most of the 32 pages that carry one, the value is a bare year and the
 * staleness is cosmetic. On the Lessons page the sentence reads "…when you
 * enter the Library today <date>", which is a false statement the day after
 * the load. So for the pages listed in `LIVE_DATE_PAGES` the transform emits
 *
 *     <span class="zg-date" data-zg-date="F jS, Y">September 8th, 2026</span>
 *
 * and this refreshes it — the same trade `assets/zg-help-date.js` makes for
 * /help/'s H1, generalised to a span anywhere in a body. The server-rendered
 * value stays inside the span: it is a real date, it is what a crawler indexes,
 * and it is what a visitor with JavaScript off reads.
 *
 * A span may also carry `data-zg-date-adj-days` — the whole-day offset of
 * WordPress's `[date adj="+1 day"]`, which is `date($format, strtotime($adj))`
 * on live and therefore also moves with the clock. It is emitted only when the
 * offset is non-zero, so every span written before 2026-09-10 takes the exact
 * path it always took: no attribute, no offset, byte-identical output. The
 * transform refuses any `adj` it cannot express in whole days
 * (`phpDateAdjustmentDays`), so an unreadable value here means something rewrote
 * the attribute — the span is left alone rather than dated a day out.
 *
 * Only the subset of PHP `date()` characters the transform itself supports is
 * implemented, and an unrecognised character aborts that span rather than
 * printing a guess — a wrong date in marketing copy is worse than a slightly
 * old one. A span whose text no longer matches what the format would have
 * produced on SOME date is left alone too, so a merchant edit survives.
 */
(() => {
  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // PHP's `S`: 11th/12th/13th are the exceptions the naive rule gets wrong.
  const ordinal = (day) => {
    if (day % 100 >= 11 && day % 100 <= 13) return 'th';
    return { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th';
  };

  /** The same subset as transform/shortcodes.ts `formatPhpDate`. */
  const format = (date, spec) => {
    let out = '';
    for (let i = 0; i < spec.length; i++) {
      const c = spec[i];
      if (c === '\\') { out += spec[i + 1] ?? ''; i++; continue; }
      if (!/[a-zA-Z]/.test(c)) { out += c; continue; }
      switch (c) {
        case 'Y': out += String(date.getFullYear()); break;
        case 'y': out += String(date.getFullYear()).slice(-2); break;
        case 'F': out += MONTHS[date.getMonth()]; break;
        case 'M': out += MONTHS[date.getMonth()].slice(0, 3); break;
        case 'n': out += String(date.getMonth() + 1); break;
        case 'm': out += String(date.getMonth() + 1).padStart(2, '0'); break;
        case 'j': out += String(date.getDate()); break;
        case 'd': out += String(date.getDate()).padStart(2, '0'); break;
        case 'S': out += ordinal(date.getDate()); break;
        case 'l': out += DAYS[date.getDay()]; break;
        case 'D': out += DAYS[date.getDay()].slice(0, 3); break;
        default: return null;
      }
    }
    return out;
  };

  /*
   * Does `text` look like something this format produced? Checked by rendering
   * the same format for a spread of dates and comparing the SHAPE — digits
   * collapsed to `#` and month/day names to their type — so any date in the
   * format matches and edited copy does not.
   */
  const shapeOf = (text) => text
    .replace(new RegExp(`\\b(${MONTHS.join('|')})\\b`, 'g'), 'MONTH')
    .replace(new RegExp(`\\b(${DAYS.join('|')})\\b`, 'g'), 'DAY')
    .replace(/\d+/g, '#')
    .replace(/\b(st|nd|rd|th)\b/g, 'ORD');

  /** `data-zg-date-adj-days` -> whole days, or null for anything unreadable. */
  const offsetDays = (span) => {
    const raw = span.getAttribute('data-zg-date-adj-days');
    if (raw === null || raw === '') return 0;
    if (!/^-?\d{1,4}$/.test(raw.trim())) return null;
    return Number(raw.trim());
  };

  const run = () => {
    const now = new Date();
    for (const span of document.querySelectorAll('[data-zg-date]')) {
      const spec = span.getAttribute('data-zg-date');
      if (!spec) continue;
      const days = offsetDays(span);
      if (days === null) continue;
      const when = days === 0 ? now : new Date(now.getFullYear(), now.getMonth(), now.getDate() + days,
        now.getHours(), now.getMinutes(), now.getSeconds());
      const fresh = format(when, spec);
      if (fresh === null) continue;

      const current = span.textContent.trim();
      if (current === fresh) continue;
      if (shapeOf(current) !== shapeOf(fresh)) continue;

      span.textContent = fresh;
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  document.addEventListener('shopify:section:load', run);
})();
